/*
 * Copyright Mimic Networks, Inc. 2026.
 */

import { ColumnsType } from 'antd/es/table';

import { ActivityLog, ActivityLogFilters, GetUserActivityResponse } from '@/client';
import { JsonTreeViewer } from '@/components/JsonTreeViewer/JsonTreeViewer';
import MimicHighlighter from '@/components/MimicHighlighter';
import { PaginatedTable } from '@/components/PaginatedTable';
import { RelativeTime } from '@/components/RelativeTime';
import { OpenAPISchema, useApiSchemaQuery } from '@/hooks/query/useApiSchemaQuery';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { Container } from '@/primitives/Container';
import { Flex } from '@/primitives/Flex';
import { Text } from '@/primitives/Text';
import { UpdateParams } from '@/utils/params';

function getEndpointDescription(
  schema: OpenAPISchema | undefined,
  endpoint: string,
  method: string = 'GET',
): string | undefined {
  // exact match
  if (schema?.paths?.[endpoint]) {
    return schema.paths?.[endpoint][method.toLowerCase()]?.description;
  }

  // regex match
  const pathMethod = Object.entries(schema?.paths || {}).find(([templatePath]) => {
    const pattern = templatePath.replace(/\{[^}]+\}/g, '[^/]+');
    const regex = new RegExp(`^${pattern}$`);
    return regex.test(endpoint);
  });

  if (!pathMethod) return undefined;

  const [, pathConfig] = pathMethod;
  return pathConfig[method.toLowerCase()]?.description;
}

interface UserActivityTableProps {
  data: GetUserActivityResponse;
  updateQueryParams: UpdateParams<ActivityLog, ActivityLogFilters>;
  filters?: ActivityLogFilters;
}

export function UserActivityTable({ data, updateQueryParams, filters }: UserActivityTableProps) {
  const { t } = useMimicTranslation('userActivity');
  const { data: apiSchema } = useApiSchemaQuery();

  const getMethodColor = (method: string): string => {
    if (method === 'GET') return '#52c41a';
    if (method === 'POST') return '#1890ff';
    if (method === 'PUT') return '#faad14';
    return '#ff4d4f';
  };

  const columns: ColumnsType<ActivityLog> = [
    {
      title: t('columns.actor'),
      dataIndex: 'actorName',
      filterMode: 'menu',
      render: (_, record: ActivityLog) => {
        return (
          <Flex>
            <Flex vertical gap="none">
              <Text size="lg">
                <MimicHighlighter searchText={filters?.actorName} text={record.actor?.displayName || ''} />
              </Text>
              {record.actor?.email && <Text style={{ color: 'rgba(0, 0, 0, 0.56)' }}>{record.actor.email}</Text>}
            </Flex>
          </Flex>
        );
      },
    },
    {
      title: t('columns.type'),
      dataIndex: 'actorType',
      filters: [
        { text: 'User', value: 'user' },
        { text: 'API Token', value: 'api-token' },
      ],
      render: (_, record: ActivityLog) => <Text>{record.actor?.type || 'N/A'}</Text>,
    },
    {
      title: t('columns.method'),
      dataIndex: 'method',
      filters: [
        { text: 'GET', value: 'GET' },
        { text: 'POST', value: 'POST' },
        { text: 'PUT', value: 'PUT' },
        { text: 'PATCH', value: 'PATCH' },
        { text: 'DELETE', value: 'DELETE' },
      ],
      render: (_, record: ActivityLog) => (
        <Text mono style={{ color: getMethodColor(record.requestMethod) }}>
          {record.requestMethod}
        </Text>
      ),
    },
    {
      title: t('columns.endpoint'),
      dataIndex: 'endpoint',
      filterMode: 'menu',
      render: (_, record: ActivityLog) => (
        <Flex vertical gap="xs">
          <Text>{getEndpointDescription(apiSchema, record.requestEndpoint, record.requestMethod)}</Text>
          <Text mono size="sm" type="subtle">
            <MimicHighlighter searchText={filters?.endpoint} text={record.requestEndpoint} />
          </Text>
        </Flex>
      ),
    },
    {
      title: t('columns.status'),
      dataIndex: 'status',
      filters: [
        { text: '200 (Success)', value: 200 },
        { text: '201 (Created)', value: 201 },
        { text: '400 (Bad Request)', value: 400 },
        { text: '401 (Unauthorized)', value: 401 },
        { text: '403 (Forbidden)', value: 403 },
        { text: '404 (Not Found)', value: 404 },
        { text: '500 (Server Error)', value: 500 },
      ],
      render: (_, record: ActivityLog) => (
        <Text
          mono
          style={{ color: record.responseStatus >= 200 && record.responseStatus < 300 ? '#52c41a' : '#ff4d4f' }}
        >
          {record.responseStatus}
        </Text>
      ),
    },
    {
      title: t('columns.duration'),
      dataIndex: 'durationMs',
      key: 'durationMs',
      render: (duration: number) => <Text mono>{duration}</Text>,
    },
    {
      title: t('columns.timestamp'),
      dataIndex: 'dateCreated',
      key: 'dateCreated',
      render: (timestamp: string) => <RelativeTime date={timestamp} />,
    },
  ];

  const expandedRowRender = (record: ActivityLog, _i: number, _j: number, expanded: boolean) => {
    if (!expanded) return null;

    const params = { ...record.requestQueryParams, ...record.requestBody };

    return (
      <Container style={{ padding: '16px', backgroundColor: 'transparent' }}>
        <Text strong style={{ display: 'block' }}>
          {t('requestParams')}
        </Text>
        <JsonTreeViewer data={params} defaultExpanded />
      </Container>
    );
  };

  return (
    <PaginatedTable
      data={data}
      rowKey={(activity) => activity.id}
      columns={columns}
      data-testid="user-activity-table"
      onParamsChange={updateQueryParams}
      expandable={{
        expandedRowRender,
        rowExpandable: (record) => !!record.requestQueryParams || !!record.requestBody,
      }}
    />
  );
}
